// See useEventTimeframe.spec.md for ETF-FR-001 and ETF-FR-002

import { useCallback } from 'react'
import type { TimeRange } from 'core/components/widgets/timeRanges'
import useScopedPreferences from 'core/session/useScopedPreferences'
import { DEFAULT_TIMEFRAME, TIMEFRAME_OPTIONS } from './timeframes'

export const EVENT_TIMEFRAME_PREFS_KEY = 'auditEvents'

interface EventTimeframePrefs extends Record<string, unknown> {
  timeframe: TimeRange
}

// Stable, so the preferences hook doesn't rebuild its prefs on every render
const DEFAULT_PREFS: Partial<EventTimeframePrefs> = {}

const isTimeframeOption = (value: unknown): value is TimeRange =>
  TIMEFRAME_OPTIONS.includes(value as TimeRange)

// ETF-FR-001: One timeframe for the Notification Bar and the Notification Center page; picking one
// in either changes both. ETF-FR-002: Kept in the user's preferences, so it is still selected after
// a reload or a new login; a missing or no-longer-offered value falls back to the default.
export default function useEventTimeframe(): [TimeRange, (timeframe: TimeRange) => void] {
  const { prefs, updatePrefs } = useScopedPreferences<EventTimeframePrefs>(
    EVENT_TIMEFRAME_PREFS_KEY,
    DEFAULT_PREFS,
  )
  const timeframe = isTimeframeOption(prefs.timeframe) ? prefs.timeframe : DEFAULT_TIMEFRAME
  const setTimeframe = useCallback(
    (value: TimeRange) => updatePrefs({ timeframe: value }),
    [updatePrefs],
  )
  return [timeframe, setTimeframe]
}
